import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Stack,
  Collapse,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import EventIcon from '@mui/icons-material/Event';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { useSelector } from 'react-redux';
import store, { RootState } from '../../store';
import { loadUser } from '../../features/auth/authSlice';
import {
  withdrawalService,
  WithdrawalDataResponse,
  WithdrawalEvent,
  PendingWithdrawal,
  WithdrawalRequestItem,
  WithheldWithdrawalBalance,
} from '../../services/dashboardService';
import { useToast } from '../../features/master_adm/hook/use-toast';
import {
  formatCurrencyBrl,
  parseAmountInput,
  validateWithdrawalAmount as validateWithdrawalAmountInput,
} from '../../utils/withdrawalAmountForm';
import PagarmeRecipientSetupForm from './components/PagarmeRecipientSetupForm';
import { normalizeWithdrawalPanelMode } from '../../utils/parishWithdrawalPanel';
import { useBrandRegistrationCopy } from '../../brands/registrationCopy';

const DEFAULT_PARISH_WITHDRAWAL_PANEL_MESSAGE =
  'O saque está temporariamente indisponível para esta paróquia. Entre em contato com o suporte da plataforma.';

const PIX_KEY_TYPE_LABEL: Record<string, string> = {
  cpf: 'CPF',
  cnpj: 'CNPJ',
  email: 'E-mail',
  phone: 'Celular',
  random: 'Chave aleatória',
};

function maskAccountEmail(email?: string) {
  if (!email || !email.includes('@')) return 'o e-mail cadastrado';
  const [local, domain] = email.split('@');
  const visible = local.slice(0, Math.min(3, local.length));
  const hidden = Math.max(3, local.length - visible.length);
  return `${visible}${'*'.repeat(hidden)}@${domain}`;
}

function formatEventDate(date?: string) {
  if (!date) return '—';
  const d = new Date(date);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
}

function EmptyWithdrawalState({
  title,
  description,
  icon,
}: {
  title: string;
  description: string;
  icon?: React.ReactNode;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 3, sm: 4 },
        textAlign: 'center',
        bgcolor: 'action.hover',
        borderStyle: 'dashed',
      }}
    >
      {icon ?? <AccountBalanceWalletIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1.5 }} />}
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: 'auto' }}>
        {description}
      </Typography>
    </Paper>
  );
}

function WithdrawalEventCard({
  event,
  expanded,
  onToggle,
  onRequest,
  disabled,
  disabledLabel,
  loading,
  formatCurrency,
}: {
  event: WithdrawalEvent;
  expanded: boolean;
  onToggle: () => void;
  onRequest: () => void;
  disabled: boolean;
  disabledLabel?: string;
  loading: boolean;
  formatCurrency: (v: number) => string;
}) {
  const t = useBrandRegistrationCopy();
  const manualOnly = event.selfServiceAvailable === false;
  const viaMercadoPago = !manualOnly && event.settlementChannel === 'mercadopago';

  return (
    <Paper
      variant="outlined"
      elevation={0}
      square
      sx={{
        overflow: 'hidden',
        border: 'none',
        borderRadius: 0,
        borderColor: expanded ? 'primary.light' : 'divider',
        bgcolor: expanded ? 'action.hover' : 'background.paper',
      }}
    >
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'minmax(0, 1fr) auto auto',
            md: 'minmax(0, 1fr) 140px 180px',
          },
          gap: { xs: 1.5, sm: 2 },
          alignItems: 'center',
        }}
      >
        <Box
          role="button"
          tabIndex={0}
          onClick={onToggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onToggle();
            }
          }}
          sx={{ minWidth: 0, cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 1 }}
        >
          <EventIcon sx={{ color: 'primary.main', mt: 0.25, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ wordBreak: 'break-word', lineHeight: 1.3 }}>
                {event.title}
              </Typography>
              {manualOnly ? (
                <Chip label="Saque manual" size="small" color="warning" variant="outlined" sx={{ height: 22 }} />
              ) : viaMercadoPago ? (
                <Chip label="Via Mercado Pago" size="small" color="info" variant="outlined" sx={{ height: 22 }} />
              ) : (
                <Chip label="Solicitar aqui" size="small" color="success" variant="outlined" sx={{ height: 22 }} />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {event.totalRegistrations} {t('inscrição(ões) paga(s)')}
              {event.endDate ? ` • Encerrado em ${formatEventDate(event.endDate)}` : ''}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
          <Typography variant="caption" color="text.secondary" display="block">
            {manualOnly ? 'Saldo (manual)' : 'Disponível'}
          </Typography>
          <Typography variant="h6" fontWeight={800} color={manualOnly ? 'warning.dark' : 'primary.main'} lineHeight={1.2} noWrap>
            {formatCurrency(event.availableBalance)}
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.75} alignItems="center" justifyContent={{ xs: 'stretch', sm: 'flex-end' }}>
          <IconButton size="small" onClick={onToggle} aria-label={expanded ? 'Recolher' : 'Ver detalhes'}>
            <ExpandMoreIcon
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s',
              }}
            />
          </IconButton>
          <Button
            variant="contained"
            size="medium"
            onClick={(e) => {
              e.stopPropagation();
              onRequest();
            }}
            disabled={disabled}
            sx={{ flex: { xs: 1, sm: 'none' }, minWidth: { sm: 148 } }}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : disabledLabel || 'Solicitar saque'}
          </Button>
        </Stack>
      </Box>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: 2, pb: 2, pt: 0, bgcolor: 'action.hover', borderTop: 1, borderColor: 'divider' }}>
          {manualOnly ? (
            <Alert severity="info" sx={{ mt: 1.5, mb: 0.5 }}>
              O repasse deste evento é feito manualmente pela plataforma. Entre em contato com o suporte para
              solicitar. Não é possível pedir saque por aqui.
            </Alert>
          ) : viaMercadoPago ? (
            <Alert severity="info" sx={{ mt: 1.5, mb: 0.5 }}>
              Este evento usa cobrança Mercado Pago. Você pode solicitar o saque por aqui; o repasse será feito
              via Mercado Pago para a chave Pix cadastrada.
            </Alert>
          ) : null}
          <Grid container spacing={2} sx={{ pt: 1.5 }}>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">
                Líquido total
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatCurrency(event.totalParishFee)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">
                Já sacado
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatCurrency(event.alreadyWithdrawn)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">
                Disponível agora
              </Typography>
              <Typography variant="body2" fontWeight={600} color="primary.main">
                {formatCurrency(event.availableBalance)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">
                {t('Inscrições pagas')}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {event.totalRegistrations}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Paper>
  );
}

function WithdrawalRequestCard({
  item,
  variant,
  formatCurrency,
  onCancel,
  cancelling,
  cancelDisabled,
}: {
  item: WithdrawalRequestItem;
  variant: 'progress' | 'done';
  formatCurrency: (v: number) => string;
  onCancel?: () => void;
  cancelling?: boolean;
  cancelDisabled?: boolean;
}) {
  const statusMeta =
    item.status === 'pending'
      ? { label: 'Aguardando análise', color: 'warning' as const }
      : item.status === 'processing'
        ? { label: 'Em processamento', color: 'info' as const }
        : item.status === 'completed'
          ? { label: 'Concluído', color: 'success' as const }
          : item.status === 'cancelled'
            ? { label: 'Cancelado', color: 'default' as const }
            : { label: 'Rejeitado', color: 'error' as const };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {variant === 'progress' ? (
              <HourglassTopIcon fontSize="small" color="action" />
            ) : item.status === 'rejected' || item.status === 'cancelled' ? (
              <CancelIcon fontSize="small" color={item.status === 'rejected' ? 'error' : 'action'} />
            ) : (
              <CheckCircleOutlineIcon fontSize="small" color="success" />
            )}
            <Typography variant="subtitle1" fontWeight={700} sx={{ wordBreak: 'break-word' }}>
              {item.eventTitle}
            </Typography>
            <Chip label={statusMeta.label} size="small" color={statusMeta.color} sx={{ height: 22 }} />
            {item.source === 'admin_manual' && (
              <Chip label="Saque manual" size="small" variant="outlined" color="info" sx={{ height: 22 }} />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            {formatCurrency(item.amount)}
            {item.requestedAt ? ` • Solicitado em ${formatEventDate(item.requestedAt)}` : ''}
            {item.completedAt ? ` • Finalizado em ${formatEventDate(item.completedAt)}` : ''}
          </Typography>
          {item.rejectionReason ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {item.rejectionReason}
            </Typography>
          ) : null}
        </Box>
        {variant === 'progress' && (item.status === 'pending' || item.status === 'processing') && onCancel ? (
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={cancelling ? <CircularProgress size={16} color="inherit" /> : <CancelIcon />}
            onClick={onCancel}
            disabled={cancelDisabled || cancelling}
            sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, flexShrink: 0 }}
          >
            Cancelar
          </Button>
        ) : null}
      </Stack>
    </Paper>
  );
}

function SummaryRow({
  label,
  value,
  hint,
  last,
}: {
  label: string;
  value: string;
  hint?: string;
  last?: boolean;
}) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      spacing={2}
      sx={{ py: 1.1, borderBottom: last ? 0 : '1px solid #d6deed' }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ pt: 0.4 }}>
        {label.toUpperCase()}
      </Typography>
      <Box sx={{ textAlign: 'right', minWidth: 0 }}>
        <Typography fontWeight={800} sx={{ wordBreak: 'break-word' }}>
          {value}
        </Typography>
        {hint ? (
          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            {hint}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  );
}

function WithdrawalRequestFlow({
  step,
  eventTitle,
  maxAmount,
  amountInput,
  amountError,
  pixDest,
  userEmail,
  parishName,
  codeSent,
  codeSentAt,
  sendingCode,
  confirming,
  tokenValue,
  blocked,
  confirmError,
  success,
  onClose,
  onAmountChange,
  onPreset,
  onContinue,
  onBack,
  onSendCode,
  onTokenChange,
  onConfirm,
  onDone,
  onViewProgress,
}: {
  step: 1 | 2 | 3;
  eventTitle: string;
  maxAmount: number;
  amountInput: string;
  amountError: string | null;
  pixDest: WithdrawalDataResponse | null;
  userEmail?: string;
  parishName?: string;
  codeSent: boolean;
  codeSentAt: number | null;
  sendingCode: boolean;
  confirming: boolean;
  tokenValue: string;
  blocked: boolean;
  confirmError: string | null;
  success: { amount: number; remaining: number; at: string } | null;
  onClose: () => void;
  onAmountChange: (value: string) => void;
  onPreset: (value: number) => void;
  onContinue: () => void;
  onBack: () => void;
  onSendCode: () => void;
  onTokenChange: (value: string) => void;
  onConfirm: () => void;
  onDone: () => void;
  onViewProgress: () => void;
}) {
  const parsed = parseAmountInput(amountInput);
  const [resendNow, setResendNow] = useState(Date.now());
  const codeRefs = React.useRef<Array<HTMLInputElement | null>>([]);
  useEffect(() => {
    if (!codeSentAt) return;
    const id = window.setInterval(() => setResendNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [codeSentAt]);
  useEffect(() => {
    if (step === 2) codeRefs.current[0]?.focus();
  }, [step]);
  const resendLeft = codeSentAt ? Math.max(0, 45 - Math.floor((resendNow - codeSentAt) / 1000)) : 0;
  const resendLabel =
    resendLeft > 0
      ? `Reenviar código em 00:${String(resendLeft).padStart(2, '0')}`
      : 'Reenviar código';
  const presets = [{ label: `Sacar tudo (${formatCurrencyBrl(maxAmount)})`, value: maxAmount }];
  const pixType = pixDest?.pixKeyType ? PIX_KEY_TYPE_LABEL[pixDest.pixKeyType] || 'Pix' : 'Pix';
  const pixLine = pixDest?.pixKeyMasked
    ? `${pixType} ${pixDest.pixKeyMasked}`
    : 'Chave Pix ainda não cadastrada';

  return (
    <>
      <Box sx={{ px: 2.5, pt: 2.25, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        {step === 2 ? (
          <Button
            onClick={onBack}
            startIcon={<ArrowBackIcon />}
            sx={{ color: 'text.primary', fontWeight: 700, minWidth: 0, px: 0.5 }}
          >
            Voltar
          </Button>
        ) : step === 1 ? (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              bgcolor: '#eee8fb',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <AccountBalanceWalletIcon sx={{ color: 'primary.main' }} />
          </Box>
        ) : (
          <Box sx={{ flex: 1 }} />
        )}
        {step !== 3 ? (
          <Box sx={{ minWidth: 0, flex: 1, pl: step === 2 ? 1 : 0, borderLeft: step === 2 ? '1px solid #d6deed' : 0 }}>
            <Typography variant="h6" fontWeight={800} lineHeight={1.2}>
              Solicitar saque
            </Typography>
            {step === 1 ? (
              <Typography variant="body2" color="text.secondary">
                Passo 1: Valor e destino
              </Typography>
            ) : null}
          </Box>
        ) : null}
        {step !== 3 ? (
          <Chip
            size="small"
            label={`Etapa ${step} de 2`}
            sx={{ bgcolor: '#eee8fb', color: 'primary.dark', fontWeight: 700, height: 28 }}
          />
        ) : null}
        <IconButton aria-label="Fechar" onClick={step === 3 ? onDone : onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 0.5, px: 2.5 }}>
        {step === 1 ? (
          <Stack spacing={2.25}>
            <Box
              sx={{
                bgcolor: '#f6f7f9',
                borderRadius: 2,
                px: 2,
                py: 1.75,
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
                <EventIcon sx={{ color: '#4b1b7a', mt: 0.25 }} fontSize="small" />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" letterSpacing={0.6} fontWeight={700}>
                    EVENTO VINCULADO
                  </Typography>
                  <Typography fontWeight={800} sx={{ wordBreak: 'break-word' }}>
                    {eventTitle}
                  </Typography>
                </Box>
              </Stack>
              <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                <Typography variant="caption" color="text.secondary" letterSpacing={0.6} fontWeight={700}>
                  SALDO DISPONÍVEL
                </Typography>
                <Typography fontWeight={800}>{formatCurrencyBrl(maxAmount)}</Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" letterSpacing={0.6} fontWeight={700} sx={{ mb: 1, display: 'block' }}>
                VALOR DO SAQUE
              </Typography>
              <Box sx={{ bgcolor: '#f6f7f9', borderRadius: 2, px: 2, py: 1.25 }}>
                <TextField
                  fullWidth
                  variant="standard"
                  value={amountInput}
                  onChange={(e) => onAmountChange(e.target.value)}
                  error={!!amountError}
                  placeholder="0,00"
                  InputProps={{
                    disableUnderline: true,
                    startAdornment: (
                      <Typography sx={{ fontSize: 28, fontWeight: 800, mr: 1, color: '#1f2735' }}>R$</Typography>
                    ),
                    sx: { fontSize: 28, fontWeight: 800 },
                  }}
                  inputProps={{ inputMode: 'decimal', 'aria-label': 'Valor do saque' }}
                />
              </Box>
              {amountError ? (
                <Typography variant="caption" color="error" sx={{ mt: 0.75, display: 'block' }}>
                  {amountError}
                </Typography>
              ) : null}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
                {presets.map((preset) => {
                  const selected =
                    parsed != null && Math.round(parsed * 100) === Math.round(preset.value * 100);
                  return (
                    <Chip
                      key={preset.label}
                      label={preset.label}
                      onClick={() => onPreset(preset.value)}
                      variant={selected ? 'filled' : 'outlined'}
                      sx={{
                        height: 32,
                        fontWeight: 600,
                        bgcolor: selected ? '#eef1f5' : 'transparent',
                        borderColor: '#d6deed',
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" letterSpacing={0.6} fontWeight={700}>
                CONTA DE DESTINO PIX
              </Typography>
              <Box
                sx={{
                  mt: 1,
                  border: '1px solid #e6ebf2',
                  borderRadius: 2,
                  px: 1.75,
                  py: 1.5,
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      bgcolor: '#f3f5f8',
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <AccountBalanceOutlinedIcon fontSize="small" />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography fontWeight={800}>Chave Pix da paróquia</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                      {pixLine}
                    </Typography>
                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                      {pixDest?.hasWithdrawalData ? (
                        <Chip
                          size="small"
                          icon={<CheckCircleIcon />}
                          label="Chave Pix cadastrada"
                          sx={{ bgcolor: '#f3f0fa', color: '#4b1b7a', height: 26, '& .MuiChip-icon': { color: '#4b1b7a' } }}
                        />
                      ) : (
                        <Chip size="small" label="Cadastre a chave em Dados para saque" color="warning" />
                      )}
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Repasse manual pela equipe
                      </Typography>
                    </Stack>
                  </Box>
                  {pixDest?.hasWithdrawalData ? <CheckCircleIcon sx={{ color: '#1f2735' }} /> : null}
                </Stack>
              </Box>
            </Box>

            <Alert
              severity="info"
              icon={<InfoOutlinedIcon />}
              sx={{ bgcolor: '#f4f1fb', color: '#321153', '& .MuiAlert-icon': { color: '#4b1b7a' } }}
            >
              Depois de avançar, você revisa o valor e a chave Pix e confirma com o código enviado por e-mail. O dinheiro
              não sai automático.
            </Alert>
          </Stack>
        ) : step === 2 ? (
          <Stack spacing={2}>
            <Box
              sx={{
                bgcolor: '#f4f7ff',
                border: '1px solid #d6deed',
                borderRadius: 2,
                px: 2,
                py: 1.5,
                display: 'flex',
                gap: 1.5,
                alignItems: 'flex-start',
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  bgcolor: '#eee8fb',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                <AccountBalanceWalletIcon sx={{ color: 'primary.main' }} fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} letterSpacing={0.4}>
                  OPERAÇÃO PIX · {eventTitle}
                </Typography>
                <Typography fontWeight={800}>Solicitação de {formatCurrencyBrl(parsed || 0)}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                  {pixLine}
                </Typography>
              </Box>
              <Chip
                size="small"
                label="Em análise"
                sx={{ bgcolor: '#eee8fb', color: 'primary.dark', fontWeight: 700, height: 26 }}
              />
            </Box>

            <Stack spacing={1.5} alignItems="center" sx={{ py: 1 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <MarkEmailReadOutlinedIcon sx={{ color: '#fff' }} />
              </Box>
              <Typography variant="h6" fontWeight={800} textAlign="center">
                Confirmação por e-mail
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 420 }}>
                Enviamos um código de 6 dígitos para {maskAccountEmail(userEmail)}.
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center">
                {Array.from({ length: 6 }, (_, index) => (
                  <Box
                    key={index}
                    component="input"
                    ref={(node: HTMLInputElement | null) => {
                      codeRefs.current[index] = node;
                    }}
                    value={tokenValue[index] || ''}
                    inputMode="numeric"
                    aria-label={`Dígito ${index + 1} do código`}
                    disabled={!codeSent || sendingCode || confirming}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const digit = event.target.value.replace(/\D/g, '').slice(-1);
                      const next = (tokenValue.slice(0, index) + digit + tokenValue.slice(index + 1)).slice(0, 6);
                      onTokenChange(next);
                      if (digit && index < 5) codeRefs.current[index + 1]?.focus();
                    }}
                    onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                      if (event.key === 'Backspace' && !tokenValue[index] && index > 0) {
                        codeRefs.current[index - 1]?.focus();
                      }
                    }}
                    onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => {
                      const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                      if (!pasted) return;
                      event.preventDefault();
                      onTokenChange(pasted);
                      codeRefs.current[Math.min(pasted.length, 5)]?.focus();
                    }}
                    sx={{
                      width: 48,
                      height: 56,
                      border: '1px solid #d6deed',
                      borderRadius: '10px',
                      bgcolor: tokenValue[index] ? '#fff' : '#f4f7ff',
                      textAlign: 'center',
                      fontSize: 22,
                      fontWeight: 800,
                      color: '#1f2735',
                      outline: 'none',
                      '&:focus': { borderColor: 'primary.main', boxShadow: '0 0 0 3px #eee8fb' },
                    }}
                  />
                ))}
              </Stack>
              <Button
                variant="text"
                size="small"
                startIcon={<AccessTimeIcon />}
                disabled={blocked || sendingCode || confirming || resendLeft > 0}
                onClick={onSendCode}
                sx={{ color: 'primary.main', fontWeight: 700 }}
              >
                {sendingCode ? 'Enviando…' : `Não recebeu? ${resendLabel}`}
              </Button>
              {confirmError ? (
                <Alert
                  severity="warning"
                  sx={{ width: '100%', textAlign: 'left' }}
                  action={
                    /em andamento/i.test(confirmError) ? (
                      <Button color="inherit" size="small" onClick={onViewProgress}>
                        Ver
                      </Button>
                    ) : undefined
                  }
                >
                  {confirmError}
                </Alert>
              ) : null}
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={2} alignItems="center" sx={{ pt: 1 }}>
            <Chip
              label="Solicitação enviada"
              sx={{ bgcolor: '#eee8fb', color: 'primary.dark', fontWeight: 700, alignSelf: 'flex-start' }}
            />
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: '#eee8fb',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 34 }} />
            </Box>
            <Typography variant="overline" color="text.secondary" fontWeight={700}>
              Solicitação registrada
            </Typography>
            <Typography variant="h5" fontWeight={800} textAlign="center">
              Saque solicitado com sucesso
            </Typography>
            <Typography variant="h4" fontWeight={800}>
              {formatCurrencyBrl(success?.amount || parsed || 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Pedido enviado em {success?.at || 'agora'}. O repasse do valor é feito pela equipe, depois da análise.
            </Typography>
            <Box sx={{ width: '100%', bgcolor: '#f4f7ff', borderRadius: 2, px: 2, py: 1.5 }}>
              <SummaryRow label="Origem" value={eventTitle} hint={`Saldo restante: ${formatCurrencyBrl(success?.remaining ?? Math.max(0, maxAmount - (parsed || 0)))}`} />
              <SummaryRow label="Destino" value={pixType} hint={pixDest?.pixKeyMasked || 'Chave Pix'} />
              <SummaryRow label="Favorecido" value={parishName || 'Paróquia'} />
              <SummaryRow label="Tarifa" value="Grátis (R$ 0,00)" last />
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2.25, pt: 0.5, justifyContent: 'space-between', gap: 1.5 }}>
        {step === 3 ? (
          <Stack spacing={1.25} sx={{ width: '100%' }}>
            <Button variant="contained" fullWidth onClick={onDone} endIcon={<ArrowForwardIcon />} sx={{ py: 1.15 }}>
              Concluir e voltar ao painel
            </Button>
            <Button variant="text" fullWidth onClick={onViewProgress} sx={{ fontWeight: 700 }}>
              Ver andamento do saque
            </Button>
          </Stack>
        ) : (
          <>
            {step === 1 ? (
              <Button onClick={onClose} variant="outlined" sx={{ px: 2.5 }}>
                Cancelar
              </Button>
            ) : (
              <Button onClick={onBack} variant="outlined" sx={{ px: 2.5, flex: 1 }}>
                Voltar
              </Button>
            )}
            {step === 1 ? (
              <Button
                variant="contained"
                onClick={onContinue}
                endIcon={sendingCode ? undefined : <ArrowForwardIcon />}
                disabled={blocked || sendingCode || !pixDest?.hasWithdrawalData}
                sx={{ px: 2.25 }}
              >
                {sendingCode ? <CircularProgress size={22} color="inherit" /> : 'Continuar para verificação'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={onConfirm}
                startIcon={<LockOutlinedIcon />}
                disabled={blocked || !codeSent || tokenValue.length < 6 || confirming}
                sx={{ px: 2.25, flex: 1.4 }}
              >
                {confirming ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  `Confirmar solicitação ${formatCurrencyBrl(parsed || 0)}`
                )}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </>
  );
}

type SaquePageProps = {
  /** Quando informado (painel do evento), mostra só saldo e histórico desse evento. */
  eventId?: string;
  eventTitle?: string;
};

const SaquePage: React.FC<SaquePageProps> = ({ eventId: scopedEventId, eventTitle: scopedEventTitle }) => {
  const t = useBrandRegistrationCopy();
  const { user } = useSelector((state: RootState) => state.auth);
  const parish = user?.parish;
  const isMainParish = parish?.isMainParish ?? true;
  const isEventScoped = Boolean(scopedEventId);
  const withdrawalPanelMode = normalizeWithdrawalPanelMode(parish?.features?.withdrawalPanelMode);
  const allowEarlyWithdrawal =
    Boolean(user?.allowWithdrawalAccess) && Boolean(parish?.features?.allowEarlyWithdrawalRequest);
  const hasWithdrawalData = parish?.hasWithdrawalData;

  type WithdrawalGlobalState = { enabled: boolean; suspended: boolean; message?: string };
  const [withdrawalGlobalState, setWithdrawalGlobalState] = useState<WithdrawalGlobalState | null>(null);
  const withdrawalsBlocked = !!withdrawalGlobalState && (!withdrawalGlobalState.enabled || withdrawalGlobalState.suspended);
  const withdrawalsDisabled = !!withdrawalGlobalState && !withdrawalGlobalState.enabled;
  const withdrawalsBlockedMessage =
    withdrawalGlobalState?.message || 'Os saques estão suspensos/desativados no momento.';

  const [recipientEditorOpen, setRecipientEditorOpen] = useState(false);
  const [eventsWithBalance, setEventsWithBalance] = useState<WithdrawalEvent[]>([]);
  const [withheldBalances, setWithheldBalances] = useState<WithheldWithdrawalBalance[]>([]);
  const [pendingWithdrawal, setPendingWithdrawal] = useState<PendingWithdrawal | null>(null);
  const [requested, setRequested] = useState<WithdrawalRequestItem[]>([]);
  const [completed, setCompleted] = useState<WithdrawalRequestItem[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [requestingEventId, setRequestingEventId] = useState<string | null>(null);
  const [cancellingRequestId, setCancellingRequestId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [withdrawStep, setWithdrawStep] = useState<1 | 2 | 3>(1);
  const [pixDest, setPixDest] = useState<WithdrawalDataResponse | null>(null);
  const [codeSentAt, setCodeSentAt] = useState<number | null>(null);
  const [withdrawSuccess, setWithdrawSuccess] = useState<{
    amount: number;
    remaining: number;
    at: string;
  } | null>(null);
  const [confirmEvent, setConfirmEvent] = useState<{
    eventId: string;
    title: string;
    maxAmount: number;
  } | null>(null);
  const [withdrawalAmountInput, setWithdrawalAmountInput] = useState('');
  const [amountError, setAmountError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [tokenValue, setTokenValue] = useState('');
  const [withdrawalCodeSent, setWithdrawalCodeSent] = useState(false);
  const [sendingWithdrawalCode, setSendingWithdrawalCode] = useState(false);
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const { toast } = useToast();

  const matchesScopedEvent = useCallback(
    (id?: string) => !scopedEventId || id === scopedEventId,
    [scopedEventId]
  );

  const isEventWithdrawalLocked = useCallback(
    (eventId: string) =>
      requested.some(
        (r) =>
          r.eventId === eventId && (r.status === 'pending' || r.status === 'processing')
      ),
    [requested]
  );

  const totalAvailable = useMemo(
    () => eventsWithBalance.reduce((sum, e) => sum + (e.availableBalance || 0), 0),
    [eventsWithBalance]
  );

  const selfServiceEvents = useMemo(
    () => eventsWithBalance.filter((e) => e.selfServiceAvailable !== false),
    [eventsWithBalance]
  );
  const manualOnlyEvents = useMemo(
    () => eventsWithBalance.filter((e) => e.selfServiceAvailable === false),
    [eventsWithBalance]
  );
  const totalSelfServiceAvailable = useMemo(
    () => selfServiceEvents.reduce((sum, e) => sum + (e.availableBalance || 0), 0),
    [selfServiceEvents]
  );
  const totalManualOnly = useMemo(
    () => manualOnlyEvents.reduce((sum, e) => sum + (e.availableBalance || 0), 0),
    [manualOnlyEvents]
  );
  const alreadyWithdrawnTotal = useMemo(
    () =>
      completed
        .filter((item) => item.status === 'completed')
        .reduce((sum, item) => sum + (item.amount || 0), 0),
    [completed]
  );

  const scopedWithheld = useMemo(() => {
    if (!isEventScoped || !scopedEventId) return null;
    return withheldBalances.find((w) => w.eventId === scopedEventId) ?? null;
  }, [isEventScoped, scopedEventId, withheldBalances]);

  const fetchWithdrawalEvents = useCallback(async () => {
    setLoadingEvents(true);
    try {
      const data = await withdrawalService.getWithdrawalEvents(scopedEventId);
      setWithdrawalGlobalState(data.withdrawalGlobalState ?? null);
      const raw = data.eventsWithBalance ?? [];
      // Sem allowEarlyWithdrawal: mostrar apenas eventos já finalizados (endDate < agora)
      // (backend também filtra; reforço no front para cache/versões antigas)
      let filtered = allowEarlyWithdrawal
        ? raw
        : raw.filter((e) => e.endDate && new Date(e.endDate) < new Date());
      if (scopedEventId) {
        filtered = filtered.filter((e) => e.eventId === scopedEventId);
      }
      setEventsWithBalance(filtered);
      setWithheldBalances(
        (data.withheldBalances ?? []).filter((w) => !scopedEventId || w.eventId === scopedEventId)
      );
      const pending = data.pendingWithdrawal ?? null;
      setPendingWithdrawal(
        pending && matchesScopedEvent(pending.eventId) ? pending : null
      );
      setRequested(data.requested ?? []);
      setCompleted((data.completed ?? []).filter((item) => matchesScopedEvent(item.eventId)));
    } catch {
      setEventsWithBalance([]);
      setWithheldBalances([]);
      setPendingWithdrawal(null);
      setRequested([]);
      setCompleted([]);
      setWithdrawalGlobalState(null);
    } finally {
      setLoadingEvents(false);
    }
  }, [allowEarlyWithdrawal, scopedEventId, matchesScopedEvent]);

  useEffect(() => {
    fetchWithdrawalEvents();
  }, [fetchWithdrawalEvents]);

  // Se os saques estiverem totalmente desativados, mostramos apenas a aba "Disponíveis".
  useEffect(() => {
    if (withdrawalsDisabled && activeTab !== 0) setActiveTab(0);
  }, [withdrawalsDisabled, activeTab]);

  const handleOpenWithdrawalModal = (ev: WithdrawalEvent) => {
    if (ev.selfServiceAvailable === false) {
      toast({
        title: 'Saque manual',
        description: 'O repasse deste evento é manual pela plataforma. Entre em contato com o suporte.',
        variant: 'destructive',
      });
      return;
    }
    if (withdrawalsBlocked) {
      toast({ title: withdrawalsBlockedMessage, variant: 'destructive' });
      return;
    }
    setConfirmEvent({ eventId: ev.eventId, title: ev.title, maxAmount: ev.availableBalance });
    setWithdrawalAmountInput(ev.availableBalance.toFixed(2));
    setAmountError(null);
    setTokenValue('');
    setWithdrawalCodeSent(false);
    setWithdrawStep(1);
    setConfirmOpen(true);
    withdrawalService
      .getWithdrawalData()
      .then(setPixDest)
      .catch(() => setPixDest(null));
  };

  const handleSendWithdrawalCode = async () => {
    if (!confirmEvent) return;
    const amountCheck = validateWithdrawalAmountInput(confirmEvent.maxAmount, withdrawalAmountInput);
    if (!amountCheck.ok) {
      setAmountError(amountCheck.message);
      toast({ title: amountCheck.message, variant: 'destructive' });
      return;
    }
    setAmountError(null);
    if (withdrawalsBlocked) {
      toast({ title: withdrawalsBlockedMessage, variant: 'destructive' });
      return;
    }
    setSendingWithdrawalCode(true);
    try {
      await withdrawalService.sendWithdrawalToken(confirmEvent.eventId, amountCheck.value);
      setWithdrawalCodeSent(true);
      setCodeSentAt(Date.now());
      setTokenValue('');
      return true;
    } catch (e: any) {
      toast({
        title: e.response?.data?.message || e.message || 'Erro ao enviar código',
        variant: 'destructive',
      });
      return false;
    } finally {
      setSendingWithdrawalCode(false);
    }
  };

  const handleCloseConfirm = () => {
    setConfirmOpen(false);
    setConfirmEvent(null);
    setWithdrawalAmountInput('');
    setAmountError(null);
    setConfirmError(null);
    setTokenValue('');
    setWithdrawalCodeSent(false);
    setCodeSentAt(null);
    setWithdrawSuccess(null);
    setWithdrawStep(1);
    setRequestingEventId(null);
  };

  const handleContinueToVerification = async () => {
    if (!confirmEvent) return;
    const amountCheck = validateWithdrawalAmountInput(confirmEvent.maxAmount, withdrawalAmountInput);
    if (!amountCheck.ok) {
      setAmountError(amountCheck.message);
      toast({ title: amountCheck.message, variant: 'destructive' });
      return;
    }
    if (!pixDest?.hasWithdrawalData) {
      toast({ title: 'Cadastre a chave Pix antes de solicitar o saque.', variant: 'destructive' });
      return;
    }
    setAmountError(null);
    if (withdrawalCodeSent || (await handleSendWithdrawalCode())) {
      setWithdrawStep(2);
    }
  };

  const applyWithdrawalPreset = (value: number) => {
    setWithdrawalAmountInput(value.toFixed(2).replace('.', ','));
    setAmountError(null);
    if (withdrawalCodeSent) {
      setWithdrawalCodeSent(false);
      setCodeSentAt(null);
      setTokenValue('');
    }
  };

  const handleConfirmWithdrawal = async () => {
    if (!withdrawalCodeSent) {
      toast({ title: 'Envie o código por e-mail antes de confirmar.', variant: 'destructive' });
      return;
    }
    if (!confirmEvent || !tokenValue.trim()) {
      toast({ title: 'Digite o código de 6 dígitos', variant: 'destructive' });
      return;
    }
    const amountCheck = validateWithdrawalAmountInput(confirmEvent.maxAmount, withdrawalAmountInput);
    if (!amountCheck.ok) {
      setAmountError(amountCheck.message);
      toast({ title: amountCheck.message, variant: 'destructive' });
      return;
    }
    setAmountError(null);
    if (withdrawalsBlocked) {
      toast({ title: withdrawalsBlockedMessage, variant: 'destructive' });
      return;
    }
    setRequestingEventId(confirmEvent.eventId);
    try {
      const { amount } = await withdrawalService.createWithdrawalRequest(
        confirmEvent.eventId,
        tokenValue.trim(),
        amountCheck.value
      );
      setWithdrawSuccess({
        amount,
        remaining: Math.max(0, Math.round((confirmEvent.maxAmount - amount) * 100) / 100),
        at: new Date().toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }),
      });
      setWithdrawStep(3);
      await fetchWithdrawalEvents();
    } catch (e: any) {
      const message = e.response?.data?.message || e.message || 'Erro ao confirmar. Verifique o código.';
      setConfirmError(message);
      toast({ title: message, variant: 'destructive', duration: 6000 });
    } finally {
      setRequestingEventId(null);
    }
  };

  const handleCancelWithdrawal = async (requestId: string) => {
    if (withdrawalsBlocked) {
      toast({ title: withdrawalsBlockedMessage, variant: 'destructive' });
      return;
    }
    setCancellingRequestId(requestId);
    try {
      await withdrawalService.cancelWithdrawalRequest(requestId);
      toast({ title: 'Solicitação cancelada. O evento voltou para Disponível.' });
      await fetchWithdrawalEvents();
    } catch (e: any) {
      toast({ title: e.response?.data?.message || 'Erro ao cancelar', variant: 'destructive' });
    } finally {
      setCancellingRequestId(null);
    }
  };

  const formatCurrency = formatCurrencyBrl;

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        width: '100%',
        maxWidth: '100%',
        py: { xs: 2, sm: 3 },
        px: { xs: 1.5, sm: 2, md: 3 },
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant={isEventScoped ? 'h5' : 'h4'} component="h1" fontWeight={800} gutterBottom>
            Saque
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isEventScoped
              ? `Saldo e histórico de saque${scopedEventTitle ? ` de “${scopedEventTitle}”` : ' deste evento'}`
              : 'Acompanhe o saldo e todo o histórico. O repasse é confirmado pela equipe.'}
          </Typography>
        </Box>
        {hasWithdrawalData && isMainParish && withdrawalPanelMode !== 'message' ? (
          <Stack direction="row" spacing={1} flexShrink={0}>
            <IconButton
              aria-label="Atualizar lista"
              onClick={() => fetchWithdrawalEvents()}
              disabled={loadingEvents}
              size="small"
              sx={{ border: 1, borderColor: 'divider' }}
            >
              {loadingEvents ? <CircularProgress size={20} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SettingsOutlinedIcon />}
              onClick={() => setRecipientEditorOpen(true)}
            >
              Dados para saque
            </Button>
          </Stack>
        ) : null}
      </Stack>

      {!isMainParish ? (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: 'action.hover',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <AccountBalanceWalletIcon sx={{ fontSize: 56, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Saque não disponível para setores
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480, mx: 'auto' }}>
            O saque é realizado apenas pela conta principal. Entre em contato com o administrador para solicitar repasses.
          </Typography>
        </Paper>
      ) : withdrawalPanelMode === 'message' ? (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'warning.light' }}>
          <Alert severity="warning" sx={{ alignItems: 'flex-start' }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Saque bloqueado para esta paróquia
            </Typography>
            <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
              {parish?.features?.withdrawalBlockedMessage?.trim() || DEFAULT_PARISH_WITHDRAWAL_PANEL_MESSAGE}
            </Typography>
          </Alert>
        </Paper>
      ) : (
        <Box>
          {!hasWithdrawalData ? (
            <Box sx={{ mb: 3 }}>
              <PagarmeRecipientSetupForm
                mode="pix-only"
                onSaved={async () => {
                  toast({
                    title: 'Chave Pix cadastrada',
                  });
                  await store.dispatch(loadUser());
                  await fetchWithdrawalEvents();
                }}
              />
            </Box>
          ) : null}
          {withdrawalsBlocked ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {withdrawalsBlockedMessage}
            </Alert>
          ) : null}

          {pendingWithdrawal && !withdrawalsBlocked ? (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                !withdrawalsDisabled ? (
                  <Button color="inherit" size="small" onClick={() => setActiveTab(1)}>
                    Ver andamento
                  </Button>
                ) : undefined
              }
            >
              Saque em andamento para <strong>{pendingWithdrawal.eventTitle}</strong> (
              {formatCurrency(pendingWithdrawal.amount)}). Novos pedidos ficam pausados até concluir ou cancelar.
            </Alert>
          ) : null}

          {scopedWithheld && !withdrawalsBlocked ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Saldo acumulado neste evento:{' '}
              <strong>{formatCurrency(scopedWithheld.accumulatedBalance)}</strong>. O saque libera após o
              encerramento
              {scopedWithheld.endDate ? ` (${formatEventDate(scopedWithheld.endDate)})` : ''}, salvo se a
              plataforma liberar <strong>saque antecipado</strong> para sua paróquia.
            </Alert>
          ) : null}

          {!loadingEvents && (eventsWithBalance.length > 0 || (isEventScoped && (requested.length > 0 || completed.length > 0 || !!scopedWithheld))) ? (
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 1.5, sm: 2 },
                mb: 2,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', md: isEventScoped ? '2fr 1fr 1fr' : '2fr 1fr 1fr' },
                gap: { xs: 1.5, md: 2 },
                alignItems: 'center',
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}>
                <PaymentsOutlinedIcon color="primary" sx={{ fontSize: 32 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {isEventScoped
                      ? scopedWithheld
                        ? 'Acumulado (libera após o evento)'
                        : manualOnlyEvents.length > 0
                          ? 'Saldo deste evento (manual)'
                          : 'Disponível neste evento'
                      : manualOnlyEvents.length > 0
                        ? 'Disponível para solicitar'
                        : 'Total disponível para saque'}
                  </Typography>
                  <Typography variant="h4" fontWeight={800} color="primary.main" lineHeight={1.15}>
                    {formatCurrency(
                      isEventScoped
                        ? scopedWithheld
                          ? scopedWithheld.accumulatedBalance
                          : totalAvailable
                        : manualOnlyEvents.length > 0
                          ? totalSelfServiceAvailable
                          : totalAvailable
                    )}
                  </Typography>
                  {!isEventScoped && manualOnlyEvents.length > 0 ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                      + {formatCurrency(totalManualOnly)} em {manualOnlyEvents.length} evento(s) com saque manual
                    </Typography>
                  ) : null}
                  {isEventScoped && alreadyWithdrawnTotal > 0 ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                      {formatCurrency(alreadyWithdrawnTotal)} já sacados neste evento
                    </Typography>
                  ) : null}
                </Box>
              </Stack>
              {isEventScoped ? (
                <>
                  <Box sx={{ borderLeft: { md: 1 }, borderColor: { md: 'divider' }, pl: { md: 2 } }}>
                    <Typography variant="caption" color="text.secondary">
                      Em andamento
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {requested.length}
                    </Typography>
                  </Box>
                  <Box sx={{ borderLeft: { md: 1 }, borderColor: { md: 'divider' }, pl: { md: 2 } }}>
                    <Typography variant="caption" color="text.secondary">
                      Concluídos
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {completed.length}
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  <Box sx={{ borderLeft: { md: 1 }, borderColor: { md: 'divider' }, pl: { md: 2 } }}>
                    <Typography variant="caption" color="text.secondary">
                      Eventos com saldo
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {eventsWithBalance.length}
                    </Typography>
                  </Box>
                  <Box sx={{ borderLeft: { md: 1 }, borderColor: { md: 'divider' }, pl: { md: 2 } }}>
                    <Typography variant="caption" color="text.secondary">
                      Em andamento
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {requested.length}
                    </Typography>
                  </Box>
                </>
              )}
            </Paper>
          ) : null}

          {loadingEvents ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
              >
                <Tab
                  label={
                    isEventScoped
                      ? 'Saldo'
                      : `Disponíveis${eventsWithBalance.length ? ` (${eventsWithBalance.length})` : ''}`
                  }
                />
                {!withdrawalsDisabled && (
                  <Tab label={`Em andamento${requested.length ? ` (${requested.length})` : ''}`} />
                )}
                {!withdrawalsDisabled && (
                  <Tab label={`Histórico${completed.length ? ` (${completed.length})` : ''}`} />
                )}
              </Tabs>

              {activeTab === 0 && (
                <Box>
                  <Accordion
                    disableGutters
                    elevation={0}
                    sx={{
                      mb: 2,
                      border: 1,
                      borderColor: 'divider',
                      '&:before': { display: 'none' },
                      borderRadius: 1,
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <InfoOutlinedIcon fontSize="small" color="action" />
                        <Typography variant="body2" fontWeight={600}>
                          Como funciona o saque
                        </Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Typography variant="body2" color="text.secondary" component="ul" sx={{ m: 0, pl: 2.5 }}>
                        <li>Um saque por vez por evento — ao solicitar, o pedido vai para &quot;Em andamento&quot;.</li>
                        <li>Você confirma por e-mail com código de 6 dígitos.</li>
                        <li>Pode sacar valor parcial e deixar saldo para depois.</li>
                        <li>
                          A equipe marca o pedido como pago e faz a transferência manualmente. Nada sai automático pelo
                          Pagar.me ou Mercado Pago.
                        </li>
                        <li>
                          {isEventScoped
                            ? 'O histórico deste evento guarda concluídos, rejeitados e cancelados.'
                            : 'O histórico guarda todos os pedidos: concluídos, rejeitados e cancelados.'}
                        </li>
                        {!allowEarlyWithdrawal ? (
                          <li>
                            {isEventScoped
                              ? 'Só é possível solicitar após o encerramento do evento, salvo se houver liberação de saque antecipado.'
                              : 'Só aparecem eventos já encerrados; saque antecipado exige liberação do admin.'}
                          </li>
                        ) : (
                          <li>Saque antecipado liberado para sua paróquia.</li>
                        )}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>

                  {manualOnlyEvents.length > 0 ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {isEventScoped
                        ? 'O repasse deste evento é manual pela plataforma. Entre em contato com o suporte para solicitar.'
                        : manualOnlyEvents.length === eventsWithBalance.length
                          ? 'Todos os saldos abaixo têm repasse manual pela plataforma. Entre em contato com o suporte para solicitar.'
                          : `${manualOnlyEvents.length} evento(s) abaixo têm saque manual. Nos demais, use o botão Solicitar saque.`}
                    </Alert>
                  ) : null}

                  {eventsWithBalance.length === 0 ? (
                    <EmptyWithdrawalState
                      title={
                        withdrawalsBlocked
                          ? 'Saques indisponíveis'
                          : scopedWithheld
                            ? 'Saldo acumulado — saque ainda bloqueado'
                            : isEventScoped
                              ? 'Sem saldo neste evento'
                              : 'Nenhum saldo para sacar'
                      }
                      description={
                        withdrawalsBlocked
                          ? withdrawalsBlockedMessage
                          : scopedWithheld
                            ? `Há ${formatCurrency(scopedWithheld.accumulatedBalance)} de ${t('inscrições pagas')} neste evento. O saque fica disponível após o encerramento${scopedWithheld.endDate ? ` em ${formatEventDate(scopedWithheld.endDate)}` : ''}, ou antes disso se o admin liberar saque antecipado.`
                            : isEventScoped
                            ? completed.length || requested.length
                              ? 'Não há saldo restante para solicitar. Consulte as abas Em andamento e Histórico deste evento.'
                              : 'Quando houver saldo elegível neste evento, ele aparecerá aqui.'
                            : t('Quando houver inscrições pagas em eventos elegíveis, os saldos aparecerão aqui — com indicação se você pode solicitar ou se o repasse é manual.')
                      }
                    />
                  ) : (
                    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                      {eventsWithBalance.map((ev, index) => (
                        <Box
                          key={ev.eventId}
                          sx={{
                            borderBottom: index < eventsWithBalance.length - 1 ? 1 : 0,
                            borderColor: 'divider',
                          }}
                        >
                          <WithdrawalEventCard
                            event={ev}
                            expanded={expandedEventId === ev.eventId}
                            onToggle={() =>
                              setExpandedEventId((prev) => (prev === ev.eventId ? null : ev.eventId))
                            }
                            onRequest={() => handleOpenWithdrawalModal(ev)}
                            disabled={
                              !hasWithdrawalData ||
                              ev.selfServiceAvailable === false ||
                              !!withdrawalsBlocked ||
                              !!requestingEventId ||
                              isEventWithdrawalLocked(ev.eventId)
                            }
                            disabledLabel={
                              ev.selfServiceAvailable === false
                                ? 'Saque manual'
                                : isEventWithdrawalLocked(ev.eventId)
                                  ? 'Saque em andamento'
                                  : withdrawalsBlocked
                                    ? 'Indisponível'
                                    : undefined
                            }
                            loading={requestingEventId === ev.eventId}
                            formatCurrency={formatCurrency}
                          />
                        </Box>
                      ))}
                    </Paper>
                  )}
                </Box>
              )}

              {!withdrawalsDisabled && activeTab === 1 && (
                <Box>
                  {requested.length === 0 ? (
                    <EmptyWithdrawalState
                      title="Nenhum saque em andamento"
                      description={
                        isEventScoped
                          ? 'Pedidos em aberto da paróquia aparecem aqui, inclusive os de outros eventos. Só pode haver um por vez.'
                          : 'Solicite na aba "Disponíveis". O pedido aparece aqui até ser concluído ou cancelado.'
                      }
                      icon={<HourglassTopIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1.5 }} />}
                    />
                  ) : (
                    requested.map((r) => (
                      <WithdrawalRequestCard
                        key={r.id}
                        item={r}
                        variant="progress"
                        formatCurrency={formatCurrency}
                        onCancel={() => handleCancelWithdrawal(r.id)}
                        cancelling={cancellingRequestId === r.id}
                        cancelDisabled={withdrawalsBlocked || !!cancellingRequestId}
                      />
                    ))
                  )}
                </Box>
              )}

              {!withdrawalsDisabled && activeTab === 2 && (
                <Box>
                  {completed.length === 0 ? (
                    <EmptyWithdrawalState
                      title={isEventScoped ? 'Sem histórico de saque' : 'Nenhum saque no histórico'}
                      description={
                        isEventScoped
                          ? 'Pedidos concluídos, rejeitados ou cancelados deste evento aparecem aqui.'
                          : 'Pedidos concluídos, rejeitados ou cancelados aparecem aqui.'
                      }
                      icon={<CheckCircleOutlineIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1.5 }} />}
                    />
                  ) : (
                    completed.map((r) => (
                      <WithdrawalRequestCard
                        key={r.id}
                        item={r}
                        variant="done"
                        formatCurrency={formatCurrency}
                      />
                    ))
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      <Dialog open={recipientEditorOpen} onClose={() => setRecipientEditorOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Dados para saque</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <PagarmeRecipientSetupForm
            key={recipientEditorOpen ? 'editor-open' : 'editor-closed'}
            onSaved={async () => {
              toast({ title: 'Cadastro atualizado' });
              await store.dispatch(loadUser());
              await fetchWithdrawalEvents();
              setRecipientEditorOpen(false);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecipientEditorOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onClose={handleCloseConfirm}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 560 } }}
      >
        {confirmEvent && (
          <WithdrawalRequestFlow
            step={withdrawStep}
            eventTitle={confirmEvent.title}
            maxAmount={confirmEvent.maxAmount}
            amountInput={withdrawalAmountInput}
            amountError={amountError}
            pixDest={pixDest}
            userEmail={user?.email}
            parishName={parish?.name}
            codeSent={withdrawalCodeSent}
            codeSentAt={codeSentAt}
            sendingCode={sendingWithdrawalCode}
            confirming={!!requestingEventId}
            tokenValue={tokenValue}
            blocked={withdrawalsBlocked}
            confirmError={confirmError}
            success={withdrawSuccess}
            onClose={handleCloseConfirm}
            onAmountChange={(value) => {
              setWithdrawalAmountInput(value);
              setAmountError(null);
              if (withdrawalCodeSent) {
                setWithdrawalCodeSent(false);
                setCodeSentAt(null);
                setTokenValue('');
              }
            }}
            onPreset={applyWithdrawalPreset}
            onContinue={handleContinueToVerification}
            onBack={() => setWithdrawStep(1)}
            onSendCode={handleSendWithdrawalCode}
            onTokenChange={(value) => {
              setTokenValue(value);
              setConfirmError(null);
            }}
            onConfirm={handleConfirmWithdrawal}
            onDone={() => {
              handleCloseConfirm();
              if (!withdrawalsDisabled) setActiveTab(1);
            }}
            onViewProgress={() => {
              handleCloseConfirm();
              if (!withdrawalsDisabled) setActiveTab(1);
            }}
          />
        )}
      </Dialog>
    </Container>
  );
};

export default SaquePage;
